import { Skeleton } from '@mui/material';
import styled, { css } from 'styled-components';
import { CHEVRON_WIDTH, ROW_HEIGHT, ROW_INLINE_PADDING, WHEN_COLUMN_WIDTH } from './rowMetrics';
import { listBand, listSheet } from './listSheet';
import { GRID_CELL_PADDING_Y, ORDER_COLUMN_SPECS } from './columnSpecs';

// Enough to fill a phone screen without pretending to know how many orders are coming.
const DEFAULT_ROW_COUNT = 6;

// A default MUI `Chip`, which is what the grid's status column renders.
const CHIP_HEIGHT = 32;

/** The grid's own type scale (`theme.typography.body2`), which is what makes a text line 20px tall. */
const GRID_LINE_HEIGHT = 1.43;

// A screenful, and the same number whatever the viewport: the overlay is clipped, not scrolled.
const GRID_ROW_COUNT = 8;

/**
 * Placeholder rows in the shape of `OrderListRow`: the time and the price in the left column, the
 * delivery method, the customer and the status in the right one. Built from the same measurements as
 * the real row — `ROW_HEIGHT`, `WHEN_COLUMN_WIDTH`, `ROW_INLINE_PADDING` and the sheet from
 * `OrdersMobileList` — so the list neither jumps nor resizes when the orders land.
 *
 * Two bars on the left rather than three: the preorder marker and the customer-orders view's date are
 * per-order conditions, and standing in for one on every row would promise a list of preorders.
 *
 * `aria-hidden` because the container above already carries `aria-busy` — a screen reader should hear
 * "busy", not a dozen empty list items.
 */
export const OrdersSkeletonRows: React.FC<{ rows?: number }> = ({ rows = DEFAULT_ROW_COUNT }) => (
  <List aria-hidden>
    {Array.from({ length: rows }, (_, index) => (
      <Item key={index}>
        <RowBox>
          <When>
            <Skeleton variant="text" width={40} />
            <Skeleton variant="text" width={56} />
          </When>
          <Details>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="45%" />
          </Details>
          {/* Reserved and left empty: the chevron is a control, not content, so there is nothing for
              a placeholder to stand in for — but its width still has to come out of the row, or the
              bars above would be wider than the text that replaces them. */}
          <ChevronSlot />
        </RowBox>
      </Item>
    ))}
  </List>
);

/**
 * The grid's `loadingOverlay`: rows in the shape of the desktop grid's, cell for cell — the `flex`,
 * `minWidth` and tablet visibility all come from `ORDER_COLUMN_SPECS`, the same list the real columns
 * are built from, so the two cannot drift apart. The two-line customer cell is the tallest thing in a
 * row and so what a row's height is measured from; matching it here is what keeps a screen of
 * placeholders the same height as the orders that replace them.
 *
 * The overlay covers only the area below the column headers, so the headers stay put while the rows are
 * being fetched.
 */
export const OrdersGridLoadingOverlay: React.FC = () => (
  <OverlayFill aria-hidden>
    {Array.from({ length: GRID_ROW_COUNT }, (_, rowIndex) => (
      <GridRow key={rowIndex}>
        {ORDER_COLUMN_SPECS.map((spec) => (
          <GridCell key={spec.field} $flex={spec.flex} $minWidth={spec.minWidth} $tabletHidden={spec.tabletHidden}>
            {CELL_PLACEHOLDERS[spec.field]}
          </GridCell>
        ))}
      </GridRow>
    ))}
  </OverlayFill>
);

// The same sheet `OrdersMobileList` draws, so the placeholders occupy the box the orders will.
const List = styled.div`
  ${listSheet}
`;

const Item = styled.div`
  ${listBand}
`;

/**
 * The real row's box: its height, its padding and the gap between its two columns. The 14px font is
 * what the row's own lines are set in, and it is what sizes the text placeholders — inheriting the
 * page's 16px made them thicker than the text they stand in for.
 */
const RowBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: ${ROW_HEIGHT};
  padding: ${ROW_INLINE_PADDING}px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const When = styled.div`
  display: flex;
  flex: 0 0 ${WHEN_COLUMN_WIDTH}px;
  flex-direction: column;
`;

const Details = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

// `OrderListRow`'s chevron slot, width for width.
const ChevronSlot = styled.span`
  flex-shrink: 0;
  width: ${CHEVRON_WIDTH}px;
`;

/**
 * One grid row: the divider every cell carries, and the type scale the grid sets on itself — stated here
 * because it is what sizes the text placeholders, and so what a row's height is measured from.
 */
const GridRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: ${GRID_LINE_HEIGHT};
`;

/**
 * A cell, with its column's `flex` and `minWidth` and MUI's own `0 10px` plus the vertical padding the
 * real cells are given, so the placeholders sit under the columns they stand in for at any width.
 *
 * `$tabletHidden` comes straight from the spec, so the columns the grid drops between sm and md are
 * the ones these rows drop too — and with the two-line customer cell gone, the rows there are a chip
 * tall in both.
 */
const GridCell = styled.div<{ $flex: number; $minWidth: number; $tabletHidden?: boolean }>`
  flex: ${({ $flex }) => $flex};
  min-width: ${({ $minWidth }) => $minWidth}px;
  padding: ${GRID_CELL_PADDING_Y}px 10px;

  ${({ theme, $tabletHidden }) =>
    $tabletHidden &&
    css`
      display: none;

      ${theme.breakpoints.up('md')} {
        display: block;
      }
    `}
`;

// The status chip's radius, as `OrderStatusChip` renders it: a pill, not a rectangle.
const ChipSkeleton = styled(Skeleton)`
  border-radius: 16px;
`;

// Opaque so the empty grid body does not show through, and clipped so a fixed row count cannot
// overflow a short viewport. No padding of its own: the cells carry the grid's 10px themselves.
const OverlayFill = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

/**
 * What stands in for each column's content, keyed by field. Two lines for the customer, a chip for the
 * status, a single text bar for the rest — the widths are proportions of the cell, so they hold at any
 * column width. Hoisted out of the render: every row draws the same shapes.
 */
const CELL_PLACEHOLDERS: Record<string, React.ReactNode> = {
  id: <Skeleton variant="text" width="60%" />,
  deliveryContact: (
    <>
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="text" width="75%" />
    </>
  ),
  orderPlaced: <Skeleton variant="text" width="80%" />,
  deliveryType: <Skeleton variant="text" width="70%" />,
  orderStatusString: <ChipSkeleton variant="rectangular" width="75%" height={CHIP_HEIGHT} />,
  shoppingCartPrice: <Skeleton variant="text" width="45%" />,
  grandTotal: <Skeleton variant="text" width="60%" />,
};
